import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAllBackups,
  bulkDeleteBackups,
  deleteBackup,
  restoreBackup,
  type AggregateBackup,
} from '../api/games';
import { usePermission } from '../hooks/usePermission';
import { useConfirm } from '../context/ConfirmDialogContext';

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtBytes(b: number): string {
  if (b >= 1_073_741_824) return `${(b / 1_073_741_824).toFixed(1)} GB`;
  if (b >= 1_048_576) return `${(b / 1_048_576).toFixed(0)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

// ── Disk Usage Bar ─────────────────────────────────────────────────────────

function DiskBar({ total, used, free }: { total: number; used: number; free: number }) {
  const pct = Math.round((used / total) * 100);
  const color = pct >= 90 ? '#e53935' : pct >= 75 ? '#ff9800' : '#4caf50';
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ minWidth: 80 }}>Disk usage</Typography>
      <Box sx={{ flexGrow: 1, bgcolor: 'action.disabledBackground', borderRadius: 1, height: 10, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, bgcolor: color, height: '100%', transition: 'width 0.3s' }} />
      </Box>
      <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'right' }}>
        {fmtBytes(used)} / {fmtBytes(total)} ({pct}% — {fmtBytes(free)} free)
      </Typography>
    </Stack>
  );
}

// ── Bulk Delete Dialog ────────────────────────────────────────────────────

function BulkDeleteDialog({
  gameIds,
  onClose,
  onDone,
}: {
  gameIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [mode, setMode] = useState<'game' | 'age'>('game');
  const [selectedGame, setSelectedGame] = useState(gameIds[0] ?? '');
  const [days, setDays] = useState('30');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      const params = mode === 'game'
        ? { game_id: selectedGame }
        : { older_than_days: parseInt(days, 10) };
      await bulkDeleteBackups(params);
      onDone();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Bulk delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Bulk Delete Backups</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Mode</InputLabel>
            <Select
              label="Mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as 'game' | 'age')}
            >
              <MenuItem value="game">All backups of a game</MenuItem>
              <MenuItem value="age">Older than N days</MenuItem>
            </Select>
          </FormControl>

          {mode === 'game' ? (
            <FormControl size="small" fullWidth>
              <InputLabel>Game</InputLabel>
              <Select
                label="Game"
                value={selectedGame}
                onChange={(e) => setSelectedGame(e.target.value)}
              >
                {gameIds.map((id) => (
                  <MenuItem key={id} value={id}>{id}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Days"
              type="number"
              size="small"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              inputProps={{ min: 1 }}
            />
          )}

          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="warning">This action cannot be undone.</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={busy || (mode === 'age' && (isNaN(parseInt(days, 10)) || parseInt(days, 10) < 1))}
          onClick={() => void handleDelete()}
          startIcon={busy ? <CircularProgress size={16} /> : <DeleteIcon />}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Restore Dialog ─────────────────────────────────────────────────────────

function RestoreDialog({
  backup,
  onClose,
  onDone,
}: {
  backup: AggregateBackup;
  onClose: () => void;
  onDone: () => void;
}) {
  const [restartAfter, setRestartAfter] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRestore = async () => {
    setBusy(true);
    setError(null);
    try {
      await restoreBackup(backup.game_id, backup.ts, restartAfter);
      onDone();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Restore Backup</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">
            This will stop <strong>{backup.game_name}</strong> and overwrite current save data.
          </Alert>
          <Typography variant="body2">
            Backup: <strong>{fmtDate(backup.created_at)}</strong> ({fmtBytes(backup.size_bytes)})
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <input
              type="checkbox"
              id="restart-after"
              checked={restartAfter}
              onChange={(e) => setRestartAfter(e.target.checked)}
            />
            <label htmlFor="restart-after">
              <Typography variant="body2">Restart server after restore</Typography>
            </label>
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={busy}
          onClick={() => void handleRestore()}
          startIcon={busy ? <CircularProgress size={16} /> : <RestoreIcon />}
        >
          Restore
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export function Backups() {
  const canRead = usePermission('backups.read');
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data, isLoading, error } = useQuery({
    queryKey: ['backups'],
    queryFn: fetchAllBackups,
    enabled: canRead,
    refetchInterval: 60_000,
  });

  const [filterGame, setFilterGame] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<AggregateBackup | null>(null);

  const deleteMutation = useMutation({
    mutationFn: ({ gameId, ts }: { gameId: string; ts: string }) => deleteBackup(gameId, ts),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backups'] }),
  });

  if (!canRead) {
    return <Alert severity="error">You do not have permission to view backups.</Alert>;
  }

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">Failed to load backups.</Alert>;
  }

  const allBackups = data?.backups ?? [];
  const gameIds = [...new Set(allBackups.map((b) => b.game_id))].sort();

  const filtered = filterGame
    ? allBackups.filter((b) => b.game_id === filterGame)
    : allBackups;

  const handleDelete = async (backup: AggregateBackup) => {
    const ok = await confirm(`Delete backup from ${fmtDate(backup.created_at)} for ${backup.game_name}?`);
    if (!ok) return;
    deleteMutation.mutate({ gameId: backup.game_id, ts: backup.ts });
  };

  const columns: GridColDef<AggregateBackup>[] = [
    {
      field: 'game_name',
      headerName: 'Game',
      width: 160,
      renderCell: (params: GridRenderCellParams<AggregateBackup>) => (
        <Chip label={params.value as string} size="small" />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Timestamp',
      flex: 1,
      renderCell: (params: GridRenderCellParams<AggregateBackup>) => fmtDate(params.value as string),
    },
    {
      field: 'size_bytes',
      headerName: 'Size',
      width: 120,
      renderCell: (params: GridRenderCellParams<AggregateBackup>) => fmtBytes(params.value as number),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<AggregateBackup>) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Restore">
            <Button size="small" onClick={() => setRestoreTarget(params.row)}>
              <RestoreIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              size="small"
              color="error"
              disabled={deleteMutation.isPending}
              onClick={() => void handleDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </Button>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Backups</Typography>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setBulkOpen(true)}
          disabled={allBackups.length === 0}
        >
          Bulk Delete
        </Button>
      </Stack>

      {data?.disk && (
        <DiskBar total={data.disk.total} used={data.disk.used} free={data.disk.free} />
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by game</InputLabel>
          <Select
            label="Filter by game"
            value={filterGame}
            onChange={(e) => setFilterGame(e.target.value)}
          >
            <MenuItem value="">All games</MenuItem>
            {gameIds.map((id) => (
              <MenuItem key={id} value={id}>{id}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
          {filtered.length} backup{filtered.length !== 1 ? 's' : ''}
          {' · '}
          {fmtBytes(filtered.reduce((s, b) => s + b.size_bytes, 0))} total
        </Typography>
      </Stack>

      <DataGrid
        rows={filtered}
        columns={columns}
        getRowId={(row) => `${row.game_id}:${row.ts}`}
        autoHeight
        disableRowSelectionOnClick
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        sx={{ bgcolor: 'background.paper' }}
      />

      {bulkOpen && (
        <BulkDeleteDialog
          gameIds={gameIds}
          onClose={() => setBulkOpen(false)}
          onDone={() => qc.invalidateQueries({ queryKey: ['backups'] })}
        />
      )}

      {restoreTarget && (
        <RestoreDialog
          backup={restoreTarget}
          onClose={() => setRestoreTarget(null)}
          onDone={() => qc.invalidateQueries({ queryKey: ['backups'] })}
        />
      )}
    </Box>
  );
}
